/**
 * Fallback confirmation for telebirr payments.
 *
 * telebirr's server-to-server callback is the primary path and confirms most
 * payments on its own. This is what we fall back to when it doesn't land — or
 * when the SuperApp reloads the H5 page out from under us, which the checkout
 * sample does by design.
 */

import { debugLog, debugWarn, debugError, describeError } from "./debug";

const SCOPE = "payment";

const MINIAPP_ENDPOINT = (process.env.REACT_APP_MINIAPP_ENDPOINT || "").trim();

// Server allows 30 requests/min per user; never poll faster than 2s.
export const POLL_INTERVAL_MS = 3000;
export const POLL_TIMEOUT_MS = 2 * 60 * 1000;

const PENDING_KEY = "telebirr:pendingPayment";

/**
 * The pending order survives in sessionStorage so a reload lands back on the
 * same invoice. merch_order_id is optional on the server (it caches the last
 * order per invoice for 24h) but we send it whenever we still have it.
 */
export const rememberPendingPayment = (pending) => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch (error) {
    debugWarn(SCOPE, "could not persist pending payment", describeError(error));
  }
};

export const readPendingPayment = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.invoiceId ? parsed : null;
  } catch (_) {
    return null;
  }
};

export const clearPendingPayment = () => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch (_) {
    /* nothing to clean up */
  }
};

/**
 * 404 (not our invoice), 403 (merch_order_id belongs to another invoice) and
 * 422 (amount mismatch) are all dead ends — retrying re-runs the same failure,
 * so the customer needs support, not a Try Again button.
 */
const TERMINAL_HTTP = { 403: true, 404: true, 422: true };

export const fetchInvoiceStatus = async ({ invoiceId, merchOrderId, token }) => {
  const query = merchOrderId
    ? `?merch_order_id=${encodeURIComponent(merchOrderId)}`
    : "";
  const url = `${MINIAPP_ENDPOINT}/api/miniapp/telebirr-invoice-status/${encodeURIComponent(
    invoiceId
  )}${query}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const json = await res.json().catch(() => null);
  return { httpStatus: res.status, json };
};

/**
 * Poll until the invoice resolves. Branches on data.status only — never on
 * trade_status and never on the HTTP code, both of which say more than they
 * mean here.
 *
 * Resolves { outcome, data, message }:
 *   paid      → the server has already activated the subscription
 *   failed    → offer a retry, which must create a brand new order
 *   support   → terminal, do not retry
 *   timeout   → still processing; we stop asking, the callback may still land
 *   cancelled → the caller aborted (unmounted)
 */
export const pollInvoiceStatus = ({
  invoiceId,
  merchOrderId,
  token,
  intervalMs = POLL_INTERVAL_MS,
  timeoutMs = POLL_TIMEOUT_MS,
  shouldContinue = () => true,
  onPending,
}) =>
  new Promise((resolve) => {
    const startedAt = Date.now();
    let attempt = 0;

    const wait = (ms) =>
      new Promise((done) => {
        setTimeout(done, ms);
      });

    const run = async () => {
      while (shouldContinue()) {
        attempt += 1;
        try {
          const { httpStatus, json } = await fetchInvoiceStatus({
            invoiceId,
            merchOrderId,
            token,
          });
          const data = (json && json.data) || null;
          debugLog(SCOPE, `invoice-status attempt ${attempt}`, {
            httpStatus,
            status: data && data.status,
            trade_status: data && data.trade_status,
            // Present when the server itself couldn't reach telebirr. Debug
            // only — this is still a "pending", not a failure to show anyone.
            gateway_code: data && data.gateway_code,
            gateway_msg: data && data.gateway_msg,
          });

          if (TERMINAL_HTTP[httpStatus]) {
            resolve({
              outcome: "support",
              httpStatus,
              data,
              message: json && json.message,
            });
            return;
          }

          if (data && data.status === "paid") {
            resolve({ outcome: "paid", data });
            return;
          }

          if (data && data.status === "failed") {
            resolve({ outcome: "failed", data });
            return;
          }

          if (data && data.status === "pending" && onPending) {
            onPending(data);
          }
        } catch (error) {
          // A dropped request inside the SuperApp WebView is common and says
          // nothing about the payment — keep asking until the deadline.
          debugWarn(SCOPE, `invoice-status attempt ${attempt} failed`, describeError(error));
        }

        if (Date.now() - startedAt + intervalMs > timeoutMs) {
          debugError(SCOPE, "invoice-status gave up", {
            invoiceId,
            attempts: attempt,
            elapsedMs: Date.now() - startedAt,
          });
          resolve({ outcome: "timeout" });
          return;
        }

        await wait(intervalMs);
      }

      resolve({ outcome: "cancelled" });
    };

    run();
  });
